/**
 * Voice AI — Guest Detail
 * Header + 3-tab routing: Profile, Conversations, Memory.
 * Loads guest data on mount, delegates to tab sub-components.
 * Dispatches: back, toast
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { guestStyles } from '../../styles/guest-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { guestService } from '../../services/guest-service.js';

import './guest-profile-tab.js';
import './guest-conversations-tab.js';
import './guest-memory-tab.js';

export class GuestDetail extends LitElement {
  static properties = {
    phone: { type: String },
    _activeTab: { type: String, state: true },
    _guest: { type: Object, state: true },
    _loading: { type: Boolean, state: true },
  };

  static styles = [designSystem, componentStyles, guestStyles];

  constructor() {
    super();
    this.phone = null;
    this._activeTab = 'profile';
    this._guest = null;
    this._loading = true;
  }

  updated(changed) {
    if (changed.has('phone') && this.phone) {
      this._loadGuest();
    }
  }

  async _loadGuest() {
    this._loading = true;
    try {
      const data = await guestService.getGuest(this.phone);
      if (data.success) {
        this._guest = data.guest;
      }
    } catch (err) {
      console.error('[GuestDetail] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _back() {
    this.dispatchEvent(new CustomEvent('back'));
  }

  _setTab(tab) {
    this._activeTab = tab;
  }

  _onToast(e) {
    this.dispatchEvent(new CustomEvent('toast', { detail: e.detail }));
  }

  _onProfileSaved() {
    this._loadGuest(); // Refresh after profile edit
  }

  _getInitials() {
    const name = this._guest?.profile?.display_name
      || this._guest?.profile?.first_name || '';
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name[0].toUpperCase();
  }

  _getDisplayName() {
    if (!this._guest?.profile) return guestService.formatPhone(this.phone);
    return this._guest.profile.display_name
      || [this._guest.profile.first_name, this._guest.profile.last_name].filter(Boolean).join(' ')
      || guestService.formatPhone(this.phone);
  }

  render() {
    if (this._loading) {
      return html`
        ${iconStyles}
        <div class="loading-spinner">
          <i class="fa-sharp fa-light fa-spinner-third"></i>
          Loading guest...
        </div>
      `;
    }

    const tabs = [
      { key: 'profile', label: 'Profile', icon: 'fa-user' },
      { key: 'conversations', label: 'Conversations', icon: 'fa-messages' },
      { key: 'memory', label: 'Memory', icon: 'fa-brain' },
    ];

    return html`
      ${iconStyles}
      <div class="detail-panel">
        <button class="detail-back-btn" @click=${this._back}>
          <i class="fa-sharp fa-light fa-arrow-left"></i>
          All Guests
        </button>

        <div class="detail-header">
          <div class="detail-avatar">${this._getInitials()}</div>
          <div class="detail-title-area">
            <div class="detail-title">${this._getDisplayName()}</div>
            <div class="detail-subtitle">${guestService.formatPhone(this.phone)}</div>
          </div>
          <div style="display:flex;gap:var(--space-2);align-items:center;">
            ${this._guest?.index ? html`
              <span style="font-size:var(--text-xs);color:var(--color-text-tertiary);">
                ${this._guest.index.call_count || 0} calls
              </span>
              <span style="font-size:var(--text-xs);color:var(--color-text-tertiary);">
                ${this._guest.index.memory_count || 0} memories
              </span>
            ` : ''}
          </div>
        </div>

        <div class="detail-tabs">
          ${tabs.map(tab => html`
            <button
              class="detail-tab ${this._activeTab === tab.key ? 'active' : ''}"
              @click=${() => this._setTab(tab.key)}
            >
              <i class="fa-sharp fa-light ${tab.icon}"></i>
              ${tab.label}
            </button>
          `)}
        </div>

        ${this._activeTab === 'profile' ? html`
          <guest-profile-tab
            .phone=${this.phone}
            .profile=${this._guest?.profile || null}
            @toast=${this._onToast}
            @profile-saved=${this._onProfileSaved}
          ></guest-profile-tab>
        ` : ''}

        ${this._activeTab === 'conversations' ? html`
          <guest-conversations-tab
            .phone=${this.phone}
          ></guest-conversations-tab>
        ` : ''}

        ${this._activeTab === 'memory' ? html`
          <guest-memory-tab
            .phone=${this.phone}
            @toast=${this._onToast}
          ></guest-memory-tab>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('guest-detail', GuestDetail);
